/**
 * Cloudinary Service
 * Handles document and image uploads to Cloudinary
 */

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const CLOUDINARY_CLOUD_NAME = process.env.REACT_APP_CLOUDINARY_CLOUD_NAME || 'dwm2tklbe'; // Updated with user credentials
// eslint-disable-next-line @typescript-eslint/no-unused-vars
const CLOUDINARY_UPLOAD_PRESET = process.env.REACT_APP_CLOUDINARY_UPLOAD_PRESET || 'sportskeeda_docs';

/**
 * Uploads a file directly to Cloudinary using signed upload parameters from backend,
 * bypassing serverless request body limits (e.g. Vercel 4.5MB payload limit).
 * 
 * @param file The file to upload
 * @param path Optional path for the folder in Cloudinary
 * @returns Promise with the secure URL of the uploaded file
 */
export const uploadToCloudinary = async (file: File, path: string = 'applications'): Promise<string> => {
  const fileSizeMB = (file.size / (1024 * 1024)).toFixed(2);
  console.log(`📤 Uploading file: ${file.name} (${fileSizeMB} MB) to path: ${path}`);

  // 1. Try Direct Signed Upload to Cloudinary first
  try {
    const sigRes = await fetch(`/api/upload/signature?folder=${encodeURIComponent(path)}`);
    if (sigRes.ok) {
      const sigData = await sigRes.json();
      if (sigData.success && sigData.signature) {
        console.log(`☁️ Uploading directly from browser to Cloudinary (${sigData.cloudName})...`);
        const directForm = new FormData();
        directForm.append('file', file);
        directForm.append('api_key', sigData.apiKey);
        directForm.append('timestamp', sigData.timestamp.toString());
        directForm.append('signature', sigData.signature);
        directForm.append('folder', sigData.folder || path);

        const cloudRes = await fetch(`https://api.cloudinary.com/v1_1/${sigData.cloudName}/auto/upload`, {
          method: 'POST',
          body: directForm,
        });

        if (cloudRes.ok) {
          const cloudData = await cloudRes.json();
          const downloadURL = cloudData.secure_url || cloudData.url;
          console.log(`✅ Direct Cloudinary upload successful for ${file.name}:`, downloadURL);
          return downloadURL;
        } else {
          const errorData = await cloudRes.json().catch(() => null);
          console.warn('⚠️ Direct Cloudinary upload returned error, falling back to server proxy:', errorData);
        }
      }
    }
  } catch (directError) {
    console.warn('⚠️ Direct Cloudinary upload failed, falling back to server proxy:', directError);
  }

  // 2. Fallback to server proxy (/api/upload)
  try {
    console.log(`📤 Uploading file to server proxy for Cloudinary: ${file.name}`);
    
    const formData = new FormData();
    formData.append('file', file);
    formData.append('path', path);

    const response = await fetch('/api/upload', {
      method: 'POST',
      body: formData,
    });

    const contentType = response.headers.get('content-type');
    
    if (!response.ok) {
      if (contentType && contentType.includes('application/json')) {
        const errorData = await response.json();
        throw new Error(errorData.error || `Upload failed with status ${response.status}`);
      } else {
        const text = await response.text();
        console.error('❌ Server returned non-JSON error:', text.slice(0, 200));
        throw new Error(`Server Error (${response.status}): The backend server might be down or misconfigured.`);
      }
    }

    if (contentType && contentType.includes('application/json')) {
      const data = await response.json();
      console.log('✅ Upload successful:', data.downloadURL);
      return data.downloadURL;
    } else {
      const text = await response.text();
      console.error('❌ Server returned non-JSON response:', text.slice(0, 200));
      throw new Error('Invalid server response: Expected JSON but received something else. Check if the backend server is running.');
    }
  } catch (error: any) {
    console.error('❌ Upload error:', error);
    throw error;
  }
};

const cloudinaryService = {
  uploadToCloudinary,
};

export default cloudinaryService;
